/**
 * 资讯中心页面组件
 * 从 ShowcaseWebsite 中提取，数据从API获取
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMenuDataSimple } from '../../../../hooks/useMenuDataSimple';
import { getNavigationMenu } from '../../../../utils/menuDataConverter';
import { useCommonConfigStore } from '../../../../store/commonConfigStore';
import { NewsItem as News } from '../../../../types';

import './NewsPage.css';

// 扩展News类型以包含需要的字段
interface ExtendedNews extends News {
    category?: string;
    publishTime?: string;
}

interface NewsPageProps {
    news: ExtendedNews[];
    onViewDetail: (item: any, category: string) => void;
}

// 从URL查询参数读取tab，默认为'work-dynamics'
const getValidTab = (tab: string | null): string => {
    if (tab && (tab === 'work-dynamics' || tab === 'industry-news' || tab === 'notices' || tab === 'contact-us')) {
        return tab;
    }
    return 'work-dynamics';
};

const NewsPage: React.FC<NewsPageProps> = ({ news, onViewDetail }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { contactInfo } = useCommonConfigStore();
    const { menuData } = useMenuDataSimple();
    
    // 从URL参数获取当前tab（每次渲染时都从URL读取，确保与URL同步）
    const selectedNewsTab = useMemo(() => {
        return getValidTab(searchParams.get('tab'));
    }, [searchParams]);
    
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    
    // 当URL参数变化时，重置页码
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedNewsTab]);
    
    // 获取导航菜单并找到当前路径对应的图标
    const navIcon = useMemo(() => {
        const navMenu = getNavigationMenu(menuData);
        const currentMenuItem = navMenu.find((item: { path: string }) => item.path === '/show/news' || item.path === 'show/news');
        return currentMenuItem?.icon || '&#xe6a8;'; // 默认使用数据库中的图标
    }, [menuData]);

    // 新闻标签从字典或配置获取（替代硬编码）
    const newsTabs = useMemo(() => {
        // 可以从字典或配置获取，这里暂时使用固定值（实际应从字典获取）
        return [
            { key: 'work-dynamics', label: '工作动态' },
            { key: 'industry-news', label: '行业新闻' },
            { key: 'notices', label: '通知公告' },
            { key: 'contact-us', label: '联系我们' }
        ];
    }, []);

    // 根据标签获取数据（从API数据过滤）并按发布时间倒序排序
    const getCurrentData = useMemo(() => {
        let filteredNews: ExtendedNews[] = [];

        switch (selectedNewsTab) {
            case 'work-dynamics':
                filteredNews = news.filter(item => (item as ExtendedNews).category === 'work_dynamics' || (item as ExtendedNews).category === '工作动态' || item.type === '工作动态');
                break;
            case 'industry-news':
                filteredNews = news.filter(item => (item as ExtendedNews).category === 'industry_news' || (item as ExtendedNews).category === '行业新闻' || item.type === '行业新闻');
                break;
            case 'notices':
                filteredNews = news.filter(item => (item as ExtendedNews).category === 'notice' || (item as ExtendedNews).category === '通知公告' || item.type === '通知公告');
                break;
            case 'contact-us':
                return [];
            default:
                filteredNews = news.filter(item => (item as ExtendedNews).category === 'work_dynamics' || (item as ExtendedNews).category === '工作动态' || item.type === '工作动态');
        }

        // 按发布时间倒序排序（最新的在前）
        const sortedNews = filteredNews.sort((a, b) => {
            const timeA = (a as ExtendedNews).publishTime ? new Date((a as ExtendedNews).publishTime!).getTime() : 0;
            const timeB = (b as ExtendedNews).publishTime ? new Date((b as ExtendedNews).publishTime!).getTime() : 0;
            // 倒序：时间大的（新的）在前
            return timeB - timeA;
        });

        return sortedNews.map(item => ({
            ...item,
            date: (item as ExtendedNews).publishTime?.substring(0, 10) || item.date || ''
        }));
    }, [selectedNewsTab, news]);

    const currentData = getCurrentData;
    const totalPages = Math.ceil(currentData.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageData = currentData.slice(startIndex, endIndex);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleTabChange = (tabKey: string) => {
        setCurrentPage(1);
        // 更新URL参数，selectedNewsTab会自动从URL参数中读取并更新
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', tabKey);
        navigate(`/show/news?${newSearchParams.toString()}`, { replace: true });
    };

    return (
        <div className="news-center-page">
            <div className="news-center-layout">
                {/* 左侧功能列表 */}
                <div className="news-center-sidebar">
                    <div className="sidebar-container">
                        <div className="sidebar-header">
                            <span className="icon-text">
                                <i 
                                    className="iconfont" 
                                    dangerouslySetInnerHTML={{ __html: navIcon }}
                                ></i>
                                <span>资讯中心</span>
                            </span>
                            <div className="header-arrow">▼</div>
                        </div>
                        <div className="sidebar-menu">
                            {newsTabs.map(tab => (
                                <div
                                    key={tab.key}
                                    className={`menu-item ${selectedNewsTab === tab.key ? 'active' : ''}`}
                                    onClick={() => handleTabChange(tab.key)}
                                >
                                    <span className="icon-text">
                                        <i className="iconfont icon-quanlingyuguimo"></i>
                                        <span>{tab.label}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 右侧内容区 */}
                <div className="news-center-main">
                    <div className="news-center-content-section">
                        <div className="news-center-header">
                            <h1 className="news-center-header-title">
                                {newsTabs.find(t => t.key === selectedNewsTab)?.label || '资讯中心'}
                            </h1>
                            <div className="news-center-header-divider"></div>
                        </div>

                        {selectedNewsTab === 'contact-us' ? (
                            <div className="contact-info-section">
                                <div className="contact-info">
                                    {contactInfo.officePhone && (
                                        <div className="contact-item">
                                            <span className="contact-label">办公室：</span>
                                            <span className="contact-value">{contactInfo.officePhone}</span>
                                        </div>
                                    )}
                                    {contactInfo.preAuditPhone && (
                                        <div className="contact-item">
                                            <span className="contact-label">预审部：</span>
                                            <span className="contact-value">{contactInfo.preAuditPhone}</span>
                                        </div>
                                    )}
                                    {contactInfo.contactAddress && (
                                        <div className="contact-item">
                                            <span className="contact-label">地址：</span>
                                            <span className="contact-value">{contactInfo.contactAddress}</span>
                                        </div>
                                    )}
                                    {contactInfo.postalCode && (
                                        <div className="contact-item">
                                            <span className="contact-label">邮编：</span>
                                            <span className="contact-value">{contactInfo.postalCode}</span>
                                        </div>
                                    )}
                                    {contactInfo.contactEmail && (
                                        <div className="contact-item">
                                            <span className="contact-label">邮箱：</span>
                                            <span className="contact-value">{contactInfo.contactEmail}</span>
                                        </div>
                                    )}
                                    {!contactInfo.officePhone && !contactInfo.preAuditPhone && !contactInfo.contactAddress && (
                                        <div className="contact-item">
                                            <span className="contact-label">办公室：</span>
                                            <span className="contact-value">0714-1234567</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="news-list-section">
                                {currentPageData.length > 0 ? (
                                    <>
                                        <div className="news-list">
                                            {currentPageData.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="news-item"
                                                    onClick={() => onViewDetail(item, selectedNewsTab)}
                                                >
                                                    <div className="news-title">{item.title}</div>
                                                    <div className="news-date">{item.date || (item as ExtendedNews).publishTime || ''}</div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* 分页组件 - 始终显示 */}
                                        {currentData.length > 0 && (
                                            <div className="pagination-container">
                                                <div className="pagination-info">
                                                    共 {currentData.length} 条记录，第 {currentPage} / {totalPages} 页
                                                </div>
                                                <div className="pagination-buttons">
                                                    <button
                                                        className="pagination-btn"
                                                        disabled={currentPage === 1}
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                    >
                                                        上一页
                                                    </button>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                        <button
                                                            key={page}
                                                            className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}
                                                    <button
                                                        className="pagination-btn"
                                                        disabled={currentPage === totalPages}
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                    >
                                                        下一页
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="no-news">暂无数据</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsPage;

